import { CartErrorMessage } from '@kouto/types';
import { setCartId } from 'actions/cart';
import { useAppState, useBrandId, useDispatch } from 'AppProvider';
import { checkoutDiagnostics } from 'features/analytics';
import useCartItems from 'hooks/useCartItems';
import usePrevious from 'hooks/usePrevious';
import { useEffect, useRef } from 'react';
import { ICartState } from 'types/cart';
import { v7 as uuidv7 } from 'uuid';
import usePaymentIntent from './usePaymentIntent';

const MAX_PAYMENT_INTENT_ATTEMPTS = 3;

const isCartNotReusableError = (
  error: ReturnType<typeof usePaymentIntent>['error'],
): boolean => {
  if (!error || error.payload?.responseStatus !== 422) return false;
  const message = typeof error.error === 'string' ? error.error : '';
  return Object.values(CartErrorMessage).some((pattern) =>
    message.includes(pattern),
  );
};

const useCartPaymentIntent = (
  cartTotal: number,
  selectedPaymentMethod: string,
) => {
  const dispatch = useDispatch();
  const brandId = useBrandId();
  const { cartId } = useCartItems();
  const isLastChangeRestored = useAppState(
    (state: { cart: ICartState }) => !!state.cart.isLastChangeRestored,
  );
  const previousCartTotal = usePrevious(cartTotal);
  const previousCartId = usePrevious(cartId);
  const attemptsRef = useRef(0);
  const lastHandledErrorRef = useRef<string | null>(null);

  const {
    paymentIntent,
    paymentIntentSecret,
    isLoading,
    isValidating,
    error,
    freezePaymentIntent,
    unfreezePaymentIntent,
  } = usePaymentIntent(selectedPaymentMethod);

  const previousIsLoading = usePrevious(isLoading);

  useEffect(() => {
    attemptsRef.current = 0;
    lastHandledErrorRef.current = null;
  }, [cartId]);

  useEffect(() => {
    if (previousCartId && previousCartId !== cartId) {
      checkoutDiagnostics.recordCartIdChange();
    }
  }, [cartId, previousCartId]);

  useEffect(() => {
    if (paymentIntentSecret) {
      attemptsRef.current = 0;
      lastHandledErrorRef.current = null;
    }
  }, [paymentIntentSecret]);

  useEffect(() => {
    // Only the tab where the guest made the change starts a new cart. Other
    // tabs adopt its cartId through storage sync; minting here too would leave
    // each tab on its own cart, swapping between them.
    if (
      typeof previousCartTotal !== 'undefined' &&
      previousCartTotal > 0 &&
      cartTotal === 0 &&
      !isLastChangeRestored
    ) {
      dispatch(
        setCartId({
          brandId,
          cartId: uuidv7(),
        }),
      );
    }
  }, [brandId, cartTotal, dispatch, isLastChangeRestored, previousCartTotal]);

  useEffect(() => {
    if (
      previousIsLoading &&
      !isLoading &&
      !paymentIntentSecret &&
      cartTotal > 0 &&
      attemptsRef.current < MAX_PAYMENT_INTENT_ATTEMPTS &&
      !error
    ) {
      setTimeout(async () => {
        await dispatch(
          setCartId({
            brandId,
            cartId: uuidv7(),
          }),
        );
        attemptsRef.current += 1;
      }, 1000);
    }
  }, [
    brandId,
    cartTotal,
    dispatch,
    isLoading,
    paymentIntentSecret,
    previousIsLoading,
    error,
  ]);

  useEffect(() => {
    if (!isCartNotReusableError(error)) return;
    if (attemptsRef.current >= MAX_PAYMENT_INTENT_ATTEMPTS) return;

    const errorSignature = `${error?.payload?.responseStatus}-${error?.error}`;
    if (lastHandledErrorRef.current === errorSignature) return;
    lastHandledErrorRef.current = errorSignature;

    setTimeout(async () => {
      await dispatch(
        setCartId({
          brandId,
          cartId: uuidv7(),
        }),
      );
      attemptsRef.current += 1;
    }, 1000);
  }, [brandId, dispatch, error]);

  return {
    paymentIntent,
    paymentIntentSecret,
    isLoading,
    isValidating,
    error,
    freezePaymentIntent,
    unfreezePaymentIntent,
  };
};

export default useCartPaymentIntent;
